// Success.tsx
import React, { useEffect, useState } from 'react';
import { Box, Typography, Card, CardContent, Button, Alert, Snackbar } from '@mui/material';
import CheckCircleTwoToneIcon from '@mui/icons-material/CheckCircleTwoTone';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import CreateIcon from '@mui/icons-material/Create';
import { useSurveyContent } from '../../../../shared/hooks';
import titleCase from '../../../../shared/helpers/titleCase';
import { useAvailabilityData } from '../Availability/hooks';
import { useFinalizeConsultation } from '../ContactInformation/hooks';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import { surveyPortalMixpanel } from '../../../../vendor/mixpanel';
import { getReferralEligibility, postPaymentInfo } from '../../../../api/payment';

type AvailabilityDay = { day: string; date: string; month: string; slots: number };

const Success: React.FC = () => {
  const { surveyContent } = useSurveyContent();
  const handleFinishConsultation = useFinalizeConsultation();
  const cdUserName = surveyContent?.cd_associate_name ?? '';
  const surveyCompensation = surveyContent?.content?.survey_compensation ?? '';
  const screenerComp = surveyContent?.screener_complete_compensation ?? 0;
  const hasCompletionPayment = surveyContent?.enable_screener_completion_payment;
  const subjectEmail = localStorage.getItem('contact_email') ?? '';
  const contactEmail = surveyContent?.cd_associate_email;
  const mailtoLink = `mailto:${contactEmail}`;
  const seid = surveyContent?.subject ?? '';
  const surveyId = surveyContent?.survey_id ?? 0;
  const path = window.location.href;
  const domain = path.split('/surveys/')[0];
  const referralLink = domain + '/referrals/v/' + seid;
  const referralCompensation = surveyContent?.content?.referral_message ?? '';

  const [availabilityDays, setAvailabilityDays] = React.useState<AvailabilityDay[]>([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [canRefer, setCanRefer] = useState<boolean>(false);
  const showPaymentBanner = surveyContent?.completed === false;

  useEffect(() => {
    if (!seid || !surveyId) {
      setCanRefer(false);
      return;
    }
    getReferralEligibility(seid, String(surveyId))
      .then((res) => setCanRefer(res.can_refer))
      .catch(() => setCanRefer(true));
  }, [seid, surveyId]);

  const [showAllDays, setShowAllDays] = React.useState(false);
  const visibleDays = showAllDays ? availabilityDays : availabilityDays.slice(0, 3);
  const redirectToAvailabilities = () => {
    const urlAux = window.location.href.replace('/c/', '/a/');
    window.location.assign(urlAux);
  };

  const getAvailability = useAvailabilityData();

  const weekdayFromISO = (iso: string) => {
    const d = new Date(`${iso}T00:00:00Z`);
    const idx = d.getUTCDay();
    const names = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return names[idx];
  };

  const getCurrentAvailability = async (id: string) => {
    const res = await getAvailability(id);
    const fetchedAvailabilities = res?.availabilities ?? [];

    if (!Array.isArray(fetchedAvailabilities) || fetchedAvailabilities.length === 0) {
      setAvailabilityDays([]);
      return;
    }

    const byDate: Record<string, number> = {};
    for (const a of fetchedAvailabilities) {
      if (!a?.date) continue;
      byDate[a.date] = (byDate[a.date] ?? 0) + 1;
    }

    const monthNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];

    const converted = Object.keys(byDate)
      .sort()
      .map((iso) => {
        const [year, mm, dd] = iso.split('-');
        const monthName = monthNames[parseInt(mm, 10) - 1];
        const wk = weekdayFromISO(iso);
        return { day: wk, date: dd, month: monthName, slots: byDate[iso] };
      });

    setAvailabilityDays(converted);
  };

  useEffect(() => {
    if (surveyContent?.subject) {
      handleFinishConsultation(surveyContent?.subject);
      getCurrentAvailability(surveyContent?.subject);
    }
  }, [surveyContent?.subject]);

  const handleShareClick = () => {
    navigator.clipboard
      .writeText(referralLink)
      .then(() => {
        setSnackbarOpen(true);
        surveyPortalMixpanel.track('Referral Link Copied', {
          SEID: seid,
          project_id: surveyId,
          incentive_amount: referralCompensation,
          referral_link: referralLink,
        });
      })
      .catch((err) => {
        console.error('Failed to copy referral link:', err);
      });
  };

  useEffect(() => {
    if (!seid) return;
    const processPayment = async () => {
      try {
        const paymentResult = await postPaymentInfo({
          request_url: path,
          email: subjectEmail,
          screener_completion_payment: true,
        });
        localStorage.setItem('screener_paid', seid);
      } catch (e) {
        console.log('Failed to process payment', e);
      }
    };
    const paidSeid = localStorage.getItem('screener_paid');
    const hasBeenPaid = paidSeid !== null && paidSeid === seid;
    if (hasCompletionPayment && screenerComp > 0 && !hasBeenPaid && showPaymentBanner) {
      processPayment();
    }
  }, [hasCompletionPayment, screenerComp, seid, showPaymentBanner]);

  return (
    <Box
      sx={{
        maxWidth: '480px',
        background: 'white',
        borderRadius: '16px',
        boxShadow:
          '0px 5px 12px 4px rgba(0, 0, 0, 0.09), 0px 3px 6px 0px rgba(0, 0, 0, 0.12), 0px 1px 2px -2px rgba(0, 0, 0, 0.16)',
        mx: 'auto',
        px: 2,
        py: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      {referralCompensation && referralCompensation !== '' && canRefer && (
        <Box
          sx={{
            backgroundColor: 'rgba(231, 237, 255, 1)',
            borderRadius: '16px',
            padding: '20px',
            width: '100%',
            textAlign: 'justify',
            display: 'flex',
            flexDirection: 'row',
            gap: '8px',
          }}
        >
          <Box>
            <PaidOutlinedIcon />
          </Box>
          <Box sx={{ textAlign: 'left' }}>
            <Typography variant="subtitle1" fontWeight="500" fontSize={'16px'} gutterBottom>
              Know other professionals who qualify for this consultation?
            </Typography>
            <Typography variant="subtitle1" fontWeight="400" fontSize={'14px'} gutterBottom>
              Earn <span style={{ fontWeight: 700 }}>${referralCompensation}</span> for each
              professional you refer who{' '}
              <span style={{ fontWeight: 700 }}>successfully completes this consultation</span>. No
              bonus for screen-outs or partial completes.
            </Typography>
            <Button variant="contained" sx={{ marginTop: '8px' }} onClick={handleShareClick}>
              Share Link
            </Button>
          </Box>
        </Box>
      )}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="success"
          sx={{
            backgroundColor: 'green',
            color: 'white',
            fontWeight: 500,
            borderRadius: '8px',
            width: 'fit-content',
          }}
        >
          Referral link copied to clipboard
        </Alert>
      </Snackbar>
      <Box display="flex" justifyContent="center">
        <CheckCircleTwoToneIcon
          sx={{
            height: '50px',
            width: '50px',
            '& path:first-of-type': { color: 'rgba(230, 245, 235, 1)' },
            '& path:last-of-type': { color: 'rgba(42, 158, 86, 1)' },
          }}
        />
      </Box>

      <Typography variant="h3" fontWeight={700} align="center">
        Application Complete!
      </Typography>

      <Typography variant="body2" align="center">
        Thank you for completing the screener. Your application will now be reviewed by our client
        for this consultation opportunity.
      </Typography>

      {/* ── Green Payment Confirmation Banner ── */}
      {hasCompletionPayment && screenerComp > 0 && showPaymentBanner && (
        <Box
          sx={{
            backgroundColor: '#e8f5e9',
            borderRadius: '12px',
            padding: '16px 20px',
            mt: 0,
          }}
        >
          <Typography variant="subtitle1" sx={{ color: '#2A9E56' }} fontWeight={500} mb={0.5}>
            {'Your '}
            <Typography
              component="span"
              variant="subtitle1"
              sx={{ color: 'success.main', fontWeight: 500 }}
            >
              ${screenerComp}
            </Typography>
            {' payment is on the way'}
          </Typography>
          {subjectEmail !== '' && (
            <Typography variant="body2" color="text.secondary">
              {"We'll send the payment email to"}
              <br />
              {subjectEmail}
            </Typography>
          )}
        </Box>
      )}

      <Card variant="outlined">
        <CardContent sx={{ backgroundColor: '#F0FAFF', borderRadius: 1 }}>
          <Typography variant="subtitle2" fontWeight={700} mb={1}>
            {"What's Next:"}
          </Typography>

          <Box display="flex" gap={1} mb={2}>
            <Box
              sx={{
                backgroundColor: 'rgba(222, 233, 252, 1)',
                color: 'rgba(15, 70, 255, 1)',
                borderRadius: '50%',
                width: 24,
                height: 24,
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 400,
                flexShrink: 0,
              }}
            >
              1
            </Box>
            <Typography variant="body2">
              Your profile will be reviewed by our client within 5 business days
            </Typography>
          </Box>

          <Box display="flex" gap={1} mb={2}>
            <Box
              sx={{
                backgroundColor: 'rgba(222, 233, 252, 1)',
                color: 'rgba(15, 70, 255, 1)',
                borderRadius: '50%',
                width: 24,
                height: 24,
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 400,
                flexShrink: 0,
              }}
            >
              2
            </Box>
            <Typography variant="body2">
              {"If selected, you'll receive a scheduling confirmation email"}
            </Typography>
          </Box>

          <Box display="flex" gap={1}>
            <Box
              sx={{
                backgroundColor: 'rgba(222, 233, 252, 1)',
                color: 'rgba(15, 70, 255, 1)',
                borderRadius: '50%',
                width: 24,
                height: 24,
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 400,
                flexShrink: 0,
              }}
            >
              3
            </Box>
            <Typography variant="body2">
              Payment (${surveyCompensation}) will be processed within 5-7 business days after the
              consultation
            </Typography>
          </Box>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ border: 'none' }}>
        <CardContent sx={{ borderRadius: 1 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="subtitle2" fontWeight={700}>
              Your Submitted Availability:
            </Typography>
            <Typography
              onClick={redirectToAvailabilities}
              variant="body2"
              sx={{
                color: 'rgba(15, 75, 255, 1)',
                fontWeight: 500,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              <CreateIcon sx={{ height: 20, width: 20 }} />
              <span>Update</span>
            </Typography>
          </Box>

          {visibleDays.map((d, i) => (
            <Box
              key={`${d.day}-${d.date}-${i}`}
              sx={{
                backgroundColor: '#F0FAFF',
                borderRadius: '12px',
                padding: '16px 16px',
                mb: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box display="flex" alignItems="center" gap={1.5}>
                <Box textAlign="center" mr={1}>
                  <Typography
                    variant="h2"
                    fontWeight={700}
                    sx={{ lineHeight: 1, color: 'rgba(37, 99, 235, 1)' }}
                  >
                    {d.date}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {d.month}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {d.day}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {d.slots} time slots selected
                  </Typography>
                </Box>
              </Box>
            </Box>
          ))}

          {availabilityDays.length > 3 && (
            <Typography
              role="button"
              tabIndex={0}
              onClick={() => setShowAllDays((s) => !s)}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setShowAllDays((s) => !s)}
              variant="body2"
              align="center"
              sx={{
                color: 'rgba(15, 75, 255, 1)',
                fontWeight: 600,
                mt: 1,
                cursor: 'pointer',
                userSelect: 'none',
                outline: 'none',
              }}
            >
              {showAllDays ? 'Show Less' : `View All ${availabilityDays.length} Days`}
            </Typography>
          )}
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography variant="subtitle2" fontWeight={700} mb={1}>
            Have Questions?
          </Typography>
          <Box display="flex" gap={2} mb={2} alignItems="center">
            <AccountCircleIcon sx={{ height: '40px', width: '40px' }} />
            <Box>
              <Typography variant="subtitle1" fontWeight={500}>
                {cdUserName && cdUserName.length > 1 ? titleCase(cdUserName) : ''}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {contactEmail}
              </Typography>
            </Box>
          </Box>
          <Button
            variant="outlined"
            color="primary"
            fullWidth
            component="a"
            href={mailtoLink}
            target="_blank"
            rel="noopener noreferrer"
          >
            CONTACT
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Success;
